import { useTranslations } from "@zapier/i18n";
import {
  HydratedTable,
  Member,
  useCreateAssetShare,
  useFetchAssetShares,
  useTableActionPermissions,
} from "@zapier/client";
import { Icon } from "@zapier/zinnia-icons";
import { Button, Link, SkeletonText, Text } from "@zapier/design-system";
import {
  ShareModal,
  useEmitChangeOwnerInteractionEvent,
  useEmitShareInteractionEvent,
} from "@zapier/shared";
import { getTableBuilderUrl, useNotification } from "@zapier/utils";
import { useEffect } from "react";

function TableShareIntro() {
  const t = useTranslations("table-share-modal");

  return (
    <Text color="TextDefault">
      <p>
        {t.rich("intro.collaboration-description", {
          learnMoreLink: (chunks) => (
            <Link
              target="_blank"
              href="https://help.zapier.com/hc/en-us/articles/16021760381453-Manage-permissions-and-sharing-in-Zapier-Tables#h_01JQBS2DVT943JSW6Z7W0NVN99"
            >
              {chunks}
            </Link>
          ),
        })}
      </p>
      <p>
        {t.rich("intro.sharing-limitations-notice", {
          notSharedText: (chunks) => (
            <Text color="inherit" fontWeight={600}>
              {chunks}
            </Text>
          ),
        })}
      </p>
    </Text>
  );
}

interface TablePublicLinkProps {
  table: HydratedTable;
  canShare: boolean;
}

const tableKindToAssetType = {
  table: "table",
  virtual_table: "table_view",
} as const;

export function TablePublicLink({ table, canShare }: TablePublicLinkProps) {
  const t = useTranslations("table-share-modal");

  const { createNotification } = useNotification();
  const assetType = tableKindToAssetType[table.kind];

  const { sharesByType, isLoading: isLoadingShares } = useFetchAssetShares(
    { assetId: table.id, assetType },
    { enabled: Boolean(table) },
  );

  const { mutateAsync: createShare, isPending: isCreatingShare } =
    useCreateAssetShare();

  const [publicShare] = sharesByType?.url_secret ?? [];

  const handleCopyLink = async (secret: string) => {
    const url = getTableBuilderUrl(table.id, { secret });
    await navigator.clipboard.writeText(url);

    createNotification({
      type: "success",
      message: t("notifications.link-copied"),
    });
  };

  const handleCreateLink = async () => {
    try {
      const share = await createShare({
        assetId: table.id,
        assetType,
        role: "guest_viewer",
        subjectType: "url_secret",
      });

      handleCopyLink(share.subjectId);
    } catch {
      createNotification({
        type: "error",
        message: t("notifications.create-link-failed"),
      });
    }
  };

  if (isLoadingShares) {
    return <SkeletonText lines={1} width="20ch" />;
  }

  if (publicShare) {
    return (
      <Button
        onClick={() => handleCopyLink(publicShare.subjectId)}
        variant="ghost"
        iconPosition="start"
      >
        <Icon name="actionLink" />
        {t("buttons.copy-view-only-link")}
      </Button>
    );
  }

  return (
    canShare && (
      <Button
        onClick={handleCreateLink}
        isPending={isCreatingShare}
        variant="ghost"
        iconPosition="start"
      >
        <Icon name="formAdd" />
        {t("buttons.create-shareable-link")}
      </Button>
    )
  );
}

export interface TableShareModalProps {
  currentTable: HydratedTable | undefined;
  isOpen: boolean;
  onCloseModal: () => void;
  onChangeOwner: (newOwner: Member) => void;
}

export function TableShareModal({
  currentTable,
  isOpen,
  onCloseModal,
  onChangeOwner,
}: TableShareModalProps) {
  const { emit } = useEmitShareInteractionEvent();
  const { emit: emitChangeOwner } = useEmitChangeOwnerInteractionEvent();

  const { canShare, isLoading, enablePermissionsCheck, canChangeOwner } =
    useTableActionPermissions(
      currentTable?.id || "",
      currentTable?.kind || "table",
    );

  useEffect(() => {
    if (isOpen) {
      enablePermissionsCheck();
    }
  }, [isOpen, enablePermissionsCheck]);

  const isTableView = currentTable?.kind === "virtual_table";

  return (
    <ShareModal
      allowSharingWithEveryone={true}
      allowSharingWithTeams={true}
      assetId={currentTable?.id ?? ""}
      assetName={currentTable?.name ?? ""}
      assetOwner={currentTable?.owner}
      assetType={tableKindToAssetType[currentTable?.kind ?? "table"]}
      canShare={!isLoading && !!canShare}
      emit={(subjectId: string) =>
        emit({
          optionSelected: "share_table",
          assetId: currentTable?.id,
          subjectId,
        })
      }
      footerContent={
        currentTable ? (
          <TablePublicLink
            table={currentTable}
            canShare={!isLoading && !!canShare}
          />
        ) : undefined
      }
      isOpen={isOpen}
      introContent={<TableShareIntro />}
      onOpenChange={(isOpen) => {
        if (!isOpen) {
          onCloseModal();
        }
      }}
      roles={isTableView ? ["viewer"] : ["editor", "viewer"]}
      canChangeOwner={canChangeOwner}
      onChangeOwner={(newOwner) => {
        onChangeOwner(newOwner);
        emitChangeOwner({
          assetId: currentTable?.id ?? "",
          subjectId: String(newOwner.id),
        });
      }}
    />
  );
}
